import { useCallback, useMemo, useState } from 'react';
import {
  MY_CALENDAR, calendarDayInZone, emptyEvent, needsSeriesScope, noPlace, ownersFor, shortName, startingAttendees, t, teamSubtitle, wallClockInZone, weekStart,
  type EventDraft, type EventReminderAnswer, type EventType, type GridEvent, type InternalAttendee, type OutsideGuest, type SeriesScope, type SuggestionRow, type TeamMember, type TeamSelection, LINKED_GROUP_TYPES, type SearchFn, RECORD_ROUTES, recordHref, type PagedRecord } from '@bms2/shared';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { EventBookingExpanded, type EventSummary } from '../../components/composites/EventBooking';
import { CalendarActions, CalendarGrid, CalendarViewControls, type OpenedEvent } from './CalendarGrid';
import { EventModal, type PlaceOption, type PlaceSearchFn } from '../../components/modals/EventModal';
import { useAddPlace } from './AddPlace';
import { PageModule } from '../../shell/PageModule';
import { PAGES_PATH, apiFetch, post, put, useApi, useRows } from './api';
import { usePersonSearch } from '../settings/api';
import styles from './CalendarGrid.module.css';
import { useSiteCascade } from './useSiteCascade';

/**
 * The Calendar archetype of the Page Module (piece 12, the Calendar page).
 * The grid is always drawn, empty or not: an empty month is a month with
 * nothing in it, not a fallback panel. Clicking a chip opens the Event Booking
 * card, Expanded, anchored to that day; the toolbar's + Add Event and a
 * right-click on an empty cell open the Add/Edit Event modal. 🔨 The desktop
 * calendar card offers no Check in action: that offer belongs to the mobile
 * app, where the person is at the site (decision 0229).
 *
 * 🔨 A series is one row whose occurrences the read generates; editing or
 * cancelling one asks this one / all of them / this and every future one
 * before it saves, and the answer rides to the server with the occurrence it
 * names (decision 0108).
 */
export const CALENDAR_ROUTE = '/calendar';

/** 🔒 The holidays across the grid on show, which every signed-in person reads (decision 1720). */
export const CALENDAR_HOLIDAYS_PATH = `${PAGES_PATH}/calendar/holidays`;

/** One holiday as the read answers it: its calendar day and its name. */
export interface CalendarHolidayRow {
  date: string;
  name: string;
}

/**
 * Each day's holiday name; two rows on one day draw as one band naming both.
 * 🔨 A row without a string day or name is passed over, so a stray answer never
 * draws a band reading nothing.
 */
export function holidayNamesByDay(rows: readonly CalendarHolidayRow[] | null | undefined): Map<string, string> {
  const days = new Map<string, string>();
  for (const row of rows ?? []) {
    if (typeof row?.date !== 'string' || typeof row?.name !== 'string' || row.name.trim() === '') continue;
    const day = row.date.slice(0, 10);
    const before = days.get(day);
    days.set(day, before ? `${before} · ${row.name}` : row.name);
  }
  return days;
}

export interface CalendarAttendeeRow {
  name: string;
  avatarColor: string;
  attending: 'NeedsAction' | 'Accepted' | 'Declined' | 'Tentative';
  /** The keys the modal restores on an edit (piece 5). */
  personId?: string | null;
  outsideContact?: string | null;
  /** A guest's own address and number, so an edit restores the whole guest (decision 0596). */
  outsideEmail?: string | null;
  outsidePhone?: string | null;
}

export interface CalendarEventRow {
  id: string;
  subject: string;
  eventType: string;
  startsAt: string;
  endsAt: string | null;
  timeZone: string;
  ownerName: string;
  ownerPersonId?: string | null;
  place: string | null;
  placeAddress?: string | null;
  /** The pin, else the address, by decision 0674's rule; what the card's Open in Maps hands over (decision 1600). */
  placeMapsQuery?: string | null;
  siteId?: string | null;
  locationId?: string | null;
  notes?: string | null;
  meetingLink?: string | null;
  callNumber?: string | null;
  callNumberExtension?: string | null;
  travelBufferMinutes?: number | null;
  parentEventId?: string | null;
  recurrenceRule?: string | null;
  /** Set on a generated occurrence: the instant the series produced it at. */
  occurrenceStartsAt?: string | null;
  cancelledAt?: string | null;
  taggedRecordType?: string | null;
  taggedRecordId?: string | null;
  taggedRecordLabel?: string | null;
  /** The event's version as the calendar read it (decision 10670; T3616), which the window's save sends back. */
  version?: number | null;
  attendees?: CalendarAttendeeRow[];
}

/**
 * Events grouped by the day they fall on in the viewer's zone, in the order
 * the day runs. 💰 Slicing the stored UTC string instead buckets a late
 * evening under tomorrow for anyone west of UTC, so the row appears under a
 * heading it does not belong to (found while building, 2026-09-09).
 */
/**
 * Linked To searches every record an event can be tagged to, through the global search, and names the type on the row's second line (decision 0275).
 * The id carries the type in front of it, so the modal reads the pair back without a second call.
 */
export function linkedSearch(apiBaseUrl: string): SearchFn {
  return async (query: string, signal: AbortSignal): Promise<SuggestionRow[]> => {
    const response = await apiFetch(`${apiBaseUrl}${PAGES_PATH}/search?q=${encodeURIComponent(query)}`, { signal });
    if (!response.ok) return [];
    const body = (await response.json()) as { hits: { id: string; group: string; label: string }[] };
    return body.hits
      .filter((hit) => LINKED_GROUP_TYPES[hit.group] !== undefined)
      .slice(0, 20)
      .map((hit) => ({ id: `${LINKED_GROUP_TYPES[hit.group]}:${hit.id}`, line1: hit.label, line2: hit.group }));
  };
}

/**
 * The place type-ahead behind the event modal's Place field (decision 0330):
 * the typed words go to the server, which matches them against Site names,
 * Location streets and towns and Company names, and answers the first twenty
 * with the site or location id and a two-line label.
 */
/**
 * 🔒 The people search behind the event window's attendee chips (T362, T540):
 * the same `usePersonSearch` hook the Calendar page and a record page's + Add
 * Event both call, so the two windows offer the same people under the same
 * names.
 */

/**
 * The names an edited event already carries for its attendees, by person (T362).
 * 🔨 A person off the internal list, such as a customer's contact, is named from
 * the event's own row, so reopening the event never draws their id in a pill.
 */
export function attendeeNamesOf(row: CalendarEventRow | null | undefined): Record<string, string> {
  return Object.fromEntries((row?.attendees ?? []).filter((a) => Boolean(a.personId)).map((a) => [a.personId!, a.name]));
}

export function placeSearch(apiBaseUrl: string): PlaceSearchFn {
  return async (query: string, signal: AbortSignal): Promise<PlaceOption[]> => {
    const response = await apiFetch(`${apiBaseUrl}${PAGES_PATH}/calendar/places?q=${encodeURIComponent(query)}`, { signal });
    if (!response.ok) return [];
    const body = (await response.json()) as { rows: PlaceOption[] };
    return body.rows;
  };
}

export function byDay(rows: readonly CalendarEventRow[], zone: string): { day: string; rows: CalendarEventRow[] }[] {
  const days = new Map<string, CalendarEventRow[]>();
  for (const row of [...rows].sort((a, b) => a.startsAt.localeCompare(b.startsAt))) {
    // The ISO day is a grouping key for the grid, not display text, so it keeps calendarDayInZone (T663).
    const day = calendarDayInZone(row.startsAt, zone);
    days.set(day, [...(days.get(day) ?? []), row]);
  }
  return [...days.entries()].map(([day, dayRows]) => ({ day, rows: dayRows }));
}

/** A grid key: a series occurrence shares its row's id with its siblings, so the key carries the occurrence too. */
export function keyOf(row: CalendarEventRow): string {
  return row.occurrenceStartsAt ? `${row.id}@${row.occurrenceStartsAt}` : row.id;
}

/**
 * 🔒 The Tagged record line opens the record for every kind that has a page (piece 13; product owner,
 * 2026-10-04). The route comes from the one shared map every list and tile reads, never a local copy:
 * the copy here knew four kinds, so a Work Order's or a Deal's tag drew as plain text.
 */
export function taggedRecordRoute(recordType: string | null | undefined, recordId: string | null | undefined): string | null {
  if (!recordType || !recordId || !(recordType in RECORD_ROUTES)) return null;
  return recordHref(recordType as PagedRecord, recordId);
}

export function toSummary(row: CalendarEventRow): EventSummary {
  return {
    id: keyOf(row),
    subject: row.cancelledAt ? `${row.subject} · ${t('event.cancelledPill')}` : row.subject,
    eventType: row.eventType as EventType,
    startsAt: new Date(row.startsAt),
    endsAt: row.endsAt ? new Date(row.endsAt) : null,
    timeZone: row.timeZone,
    location: row.place,
    locationAddress: row.placeAddress ?? null,
    locationMapsQuery: row.placeMapsQuery ?? null,
    meetingLink: row.meetingLink ?? null,
    callNumber: row.callNumber ?? null,
    callNumberExtension: row.callNumberExtension ?? null,
    attendees: (row.attendees ?? []).map((a) => ({ person: { displayName: a.name, avatarColor: a.avatarColor }, attending: a.attending })),
    owner: row.ownerName,
    notes: row.notes ?? null,
    taggedRecordType: row.taggedRecordType ?? null,
    taggedRecordLabel: row.taggedRecordLabel ?? null,
    taggedRecordRoute: taggedRecordRoute(row.taggedRecordType, row.taggedRecordId),
  };
}

/**
 * The row as the modal edits it; a field the list does not carry opens empty.
 * 🔨 Start and End are wall clocks in the event's own zone, not a slice of the
 * stored UTC string: the row is held in Chicago and reads nine in the morning
 * there, whoever opens it (piece 2).
 */
export function toDraft(row: CalendarEventRow, ownerPersonId: string, zone: string): EventDraft {
  return {
    ...emptyEvent(ownerPersonId, zone),
    subject: row.subject,
    eventType: row.eventType as EventDraft['eventType'],
    place: { siteId: row.siteId ?? null, locationId: row.locationId ?? null },
    startsAt: wallClockInZone(row.startsAt, row.timeZone),
    endsAt: row.endsAt ? wallClockInZone(row.endsAt, row.timeZone) : '',
    timeZone: row.timeZone,
    notes: row.notes ?? null,
    meetingLink: row.meetingLink ?? null,
    callNumber: row.callNumber ?? null,
    callNumberExtension: row.callNumberExtension ?? null,
    taggedRecordType: row.taggedRecordType ?? null,
    taggedRecordId: row.taggedRecordId ?? null,
    taggedRecordLabel: row.taggedRecordLabel ?? null,
    // 🔒 The version read with the row rides back on the save, so a save over somebody else's change is refused (T3616).
    version: row.version ?? null,
    // 💰 The attendees did not travel into the draft, so an edit showed none and saved none (product owner, 2026-09-10).
    attendeePersonIds: (row.attendees ?? []).map((a) => a.personId).filter((id): id is string => Boolean(id)),
    // Each guest comes back whole — name, address, number and the answer they gave (decision 0596).
    outsideContacts: (row.attendees ?? [])
      .filter((a) => !a.personId && Boolean(a.outsideContact))
      .map((a): OutsideGuest => ({ name: a.outsideContact!, email: a.outsideEmail ?? null, phone: a.outsidePhone ?? null, attending: a.attending })),
  };
}

export interface CalendarPageProps {
  apiBaseUrl?: string;
  /** The person whose calendar this is, and who owns a new event until a session names one. */
  personId?: string;
  /** The viewer's own zone, which the grid renders in and a new event defaults to. */
  timeZone?: string;
}

export function CalendarPage({ apiBaseUrl = '', personId = '', timeZone: zone = 'UTC' }: CalendarPageProps) {
  /**
   * 🔨 Today is read in the viewer's own zone, so the marker follows the person
   * and not the machine the browser happens to be on (decision 0261).
   */
  const today = useMemo(() => calendarDayInZone(new Date(), zone), [zone]);
  const [monthStart, setMonthStart] = useState(() => `${today.slice(0, 7)}-01`);
  const [weekOf, setWeekOf] = useState(() => weekStart(today));
  const [scale, setScale] = useState<'month' | 'week'>('month');
  /**
   * 🔨 A cancelled meeting stays on the grid, drawn faint and struck through,
   * until the control hides it (decision 0267); the read asks for it rather
   * than the page filtering after the fact, so a hidden row never reaches the
   * browser (decision 0262).
   */
  const [showCancelled, setShowCancelled] = useState(true);
  const [selection, setSelection] = useState<TeamSelection>(MY_CALENDAR);
  /**
   * The read covers the whole grid, not the month: the six rows reach into the
   * month before and the month after, and an event in those cells is a real
   * one the reader can see and open. 🔨 Whose events: the viewer's own and the
   * overlaid reports', or the one person in View Only (piece 12, Team View).
   */
  const from = scale === 'week' ? weekOf : weekStart(monthStart);
  const days = scale === 'week' ? 7 : 42;
  const owners = personId ? ownersFor(personId, selection).join(',') : '';
  const path = useMemo(
    () => `${PAGES_PATH}/calendar?from=${from}&days=${days}${showCancelled ? '&cancelled=true' : ''}${owners ? `&owners=${owners}` : ''}`,
    [from, days, showCancelled, owners],
  );
  /**
   * 🔒 The read answers the rows and what it refused to read (decision 0602):
   * the banner says a selection was dropped rather than drawing the viewer's own
   * calendar under a heading naming somebody else.
   */
  const { state, reload } = useApi<{ rows: CalendarEventRow[]; dropped?: string[] }>(path, apiBaseUrl);
  const rows = useMemo(() => (state.kind === 'ready' ? state.data.rows : []), [state]);
  const dropped = state.kind === 'ready' ? (state.data.dropped ?? []) : [];
  /** 🔒 The holiday bands read the same span as the events, from the one Holiday table (piece 15; decision 1720). */
  const holidayRead = useApi<{ rows: CalendarHolidayRow[] }>(`${CALENDAR_HOLIDAYS_PATH}?from=${from}&days=${days}`, apiBaseUrl);
  const holidays = useMemo(() => holidayNamesByDay(holidayRead.state.kind === 'ready' ? holidayRead.state.data?.rows : null), [holidayRead.state]);
  const team = useRows<TeamMember>(`${PAGES_PATH}/calendar/team`, apiBaseUrl);
  const members = useMemo(() => (team.state.kind === 'ready' ? team.state.data : []), [team.state]);

  const [editing, setEditing] = useState<CalendarEventRow | null>(null);
  /** 🔨 Clicking a chip opens the Expanded card, anchored to the day, which is where Edit lives (piece 12). */
  const [opened, setOpened] = useState<OpenedEvent | null>(null);
  /** Opening an empty cell starts an event on that day, pre-filled (piece 12). */
  const [addingOn, setAddingOn] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 🔒 A save refused because the event moved since it was read (T3616): the banner offers Reload rather than Dismiss. */
  const [stale, setStale] = useState(false);
  const open = adding || addingOn !== null || editing !== null;
  /** The places and attendees feed only the Event modal, so they are read when it opens (T647). */
  const places = useRows<PlaceOption>(open ? `${PAGES_PATH}/calendar/places` : null, apiBaseUrl);
  const attendees = useRows<InternalAttendee>(open ? `${PAGES_PATH}/calendar/attendees` : null, apiBaseUrl);
  /** The card's own answer for the event Edit was pressed on, so the modal does not read the same reminder again (T647). */
  const [cardAnswer, setCardAnswer] = useState<{ eventId: string; answer: EventReminderAnswer } | null>(null);
  const seeded = editing !== null && cardAnswer !== null && cardAnswer.eventId === editing.id ? cardAnswer.answer : null;
  /**
   * 🔒 The viewer's own reminder (decision 1081): read for the event the modal
   * edits, or the channels alone for a new one, and for the event whose card
   * is open, so the card names it under the time.
   */
  const reminderAnswer = useApi<EventReminderAnswer>(open && !seeded ? `${PAGES_PATH}/calendar/reminder/own${editing ? `?eventId=${editing.id}` : ''}` : null, apiBaseUrl);
  const openedRow = opened ? (rows.find((row) => keyOf(row) === opened.eventKey) ?? null) : null;
  const openedReminder = useApi<EventReminderAnswer>(openedRow && !openedRow.recurrenceRule ? `${PAGES_PATH}/calendar/reminder/own?eventId=${openedRow.id}` : null, apiBaseUrl);
  const cardReminder = openedReminder.state.kind === 'ready' ? openedReminder.state.data.reminder : null;

  const byKey = useMemo(() => new Map(rows.map((row) => [keyOf(row), row])), [rows]);
  const overlaying = selection.overlay.length > 0 && !selection.viewOnly;
  const ownerColors = useMemo(() => {
    const colors = new Map<string, string>();
    if (personId) colors.set(personId, 'var(--navy-primary)');
    for (const member of members) colors.set(member.personId, `var(--${member.avatarColor})`);
    return colors;
  }, [members, personId]);
  const gridEvents: GridEvent[] = useMemo(
    () =>
      rows.map((row) => {
        const owner = row.ownerPersonId && row.ownerPersonId !== personId ? members.find((m) => m.personId === row.ownerPersonId) : null;
        return {
          id: keyOf(row),
          subject: row.subject,
          eventType: row.eventType,
          startsAt: row.startsAt,
          endsAt: row.endsAt,
          timeZone: row.timeZone,
          cancelledAt: row.cancelledAt ?? null,
          ownerPersonId: row.ownerPersonId ?? null,
          ownerLabel: overlaying && owner ? shortName(owner.name) : null,
          travelBufferMinutes: row.travelBufferMinutes ?? null,
          place: row.place ?? null,
          meetingLink: row.meetingLink ?? null,
          callNumber: row.callNumber ?? null,
          callNumberExtension: row.callNumberExtension ?? null,
          notes: row.notes ?? null,
          attendees: (row.attendees ?? []).map((a) => ({ displayName: a.name, avatarColor: a.avatarColor, attending: a.attending })),
        };
      }),
    [rows, members, personId, overlaying],
  );

  /** The event window's opening: a different row, a new event or a close each name a new one (T3562). */
  const windowKey = open ? (editing ? keyOf(editing) : (addingOn ?? 'new')) : 'closed';
  /** 🔒 Keyed to the window's opening, so a refusal held for one opening is read again at the next (T3562). */
  const resolveSiteCascade = useSiteCascade(apiBaseUrl, windowKey);

  const searchLinked = useMemo(() => linkedSearch(apiBaseUrl), [apiBaseUrl]);
  const searchPlaces = useMemo(() => placeSearch(apiBaseUrl), [apiBaseUrl]);
  // Add New on the Place field opens the + Add Site combo modal over the event (decision 0348).
  const addPlace = useAddPlace(apiBaseUrl);
  const searchPeople = usePersonSearch(apiBaseUrl);

  const save = useCallback(
    async (draft: EventDraft, scope: SeriesScope | null) => {
      setError(null);
      setStale(false);
      const answer = editing
        ? await put(`${PAGES_PATH}/calendar/events/${editing.id}`, { draft, scope, occurrenceStartsAt: editing.occurrenceStartsAt ?? null }, apiBaseUrl)
        : await post(`${PAGES_PATH}/calendar/events`, { draft }, apiBaseUrl);
      if (!answer.ok) {
        setError(answer.message);
        setStale(answer.status === 409);
        return;
      }
      setEditing(null);
      setAdding(false);
      setAddingOn(null);
      reload();
    },
    [apiBaseUrl, editing, reload],
  );

  /** 🔨 A cancellation sets cancelledAt; the row stays and no date array exists. */
  const cancel = useCallback(
    async (scope: SeriesScope | null) => {
      if (!editing) return;
      const answer = await post(`${PAGES_PATH}/calendar/events/${editing.id}/cancel`, { scope, occurrenceStartsAt: editing.occurrenceStartsAt ?? null }, apiBaseUrl);
      if (!answer.ok) {
        setError(answer.message);
        return;
      }
      setEditing(null);
      reload();
    },
    [apiBaseUrl, editing, reload],
  );

  /** 🔨 Reinstating clears the stamp and leaves the meeting as it stood. */
  const reinstate = useCallback(async () => {
    if (!editing) return;
    const answer = await post(`${PAGES_PATH}/calendar/events/${editing.id}/reinstate`, {}, apiBaseUrl);
    if (!answer.ok) {
      setError(answer.message);
      return;
    }
    setEditing(null);
    reload();
  }, [apiBaseUrl, editing, reload]);

  /**
   * 🔨 The Expanded card carries the Site Requirements band (decision 0134):
   * the resolver's Equipment rows are the bring list. It is read at open and
   * never copied at save.
   */
  const bandFor = useCallback(
    (siteId: string) => {
      const resolved = resolveSiteCascade(siteId);
      return resolved ? resolved.requirements.map((row) => ({ label: row.name })) : null;
    },
    [resolveSiteCascade],
  );

  const renderCard = useCallback(
    (which: OpenedEvent) => {
      const row = byKey.get(which.eventKey);
      if (!row) return null;
      /** 🔨 Another person's event in Team View is read-only: the card shows and Edit is withheld (piece 12). */
      const editable = (!row.ownerPersonId || row.ownerPersonId === personId) && offersEditMeeting(row);
      return (
        <>
          <EventBookingExpanded
            event={toSummary(row)}
            siteRequirements={row.siteId ? bandFor(row.siteId) : null}
            reminder={cardReminder}
            onEdit={
              editable
                ? () => {
                    setCardAnswer(openedReminder.state.kind === 'ready' ? { eventId: row.id, answer: openedReminder.state.data } : null);
                    setEditing(row);
                    setOpened(null);
                  }
                : undefined
            }
          />
          {!editable ? <p className={styles.readOnly}>{t(readOnlyLine(row))}</p> : null}
        </>
      );
    },
    [bandFor, byKey, personId, cardReminder],
  );

  /**
   * 🔨 A new event opens with the person creating it already an attendee, the
   * way the Person and Lead pages' own + Add Event does (product owner,
   * 2026-09-20). 💰 The calendar's modal opened with none and the server added
   * the owner on save, so one modal behaved two ways.
   */
  const initial = editing
    ? toDraft(editing, personId, zone)
    : { ...emptyEvent(personId, zone), place: noPlace(), attendeePersonIds: startingAttendees(personId), ...(addingOn ? { startsAt: `${addingOn}T09:00`, endsAt: `${addingOn}T10:00` } : {}) };
  const existing = editing ? { parentEventId: editing.parentEventId ?? null, recurrenceRule: editing.recurrenceRule ?? null, cancelledAt: editing.cancelledAt ?? null } : null;

  /** 🔨 The banner subtitle always states the mode and whose calendars are showing (piece 12, Team View); the month is the banner's own heading (decision 0329). */
  const modeSubtitle = teamSubtitle(selection, members, { overlay: t('calendar.overlay'), myCalendar: t('calendar.myCalendar'), viewing: t('calendar.viewing'), calendarOf: t('calendar.calendarOf') });
  const subtitle = modeSubtitle ?? t('calendar.subtitle');
  const teamControl = personId ? { viewerPersonId: personId, members, selection, onSelectionChange: setSelection } : null;

  return (
    <PageModule
      title={t('calendar.title')}
      subtitle={subtitle}
      controls={<CalendarViewControls monthStart={monthStart} weekOf={weekOf} today={today} scale={scale} onMonthChange={setMonthStart} onWeekChange={setWeekOf} onScaleChange={setScale} />}
    >
      <div className={styles.page} data-page="calendar" data-calendar-mode={selection.viewOnly ? 'viewOnly' : overlaying ? 'overlay' : 'mine'}>
        {/* 🔨 Team, + Add Event and Show cancelled stand in one row directly under the banner (decision 0329). */}
        <CalendarActions team={teamControl} showCancelled={showCancelled} onShowCancelledChange={setShowCancelled} onAddEvent={() => setAdding(true)} />
        {/* 🔒 A stale save's refusal offers Reload, which reads the grid again so the next open carries the event as it now stands (T3616). */}
        {error && (
          <AlertBanner
            tone="error"
            message={error}
            action={stale ? { label: t('calendar.reload'), onAction: () => { setError(null); setStale(false); reload(); } } : { label: t('modal.dismiss'), onAction: () => setError(null) }}
          />
        )}
        {/* 🔨 A selection the server would not read is named, not silently replaced (decision 0602). */}
        {dropped.length > 0 ? (
          <AlertBanner
            tone="warning"
            message={`${t('calendar.droppedSelection')} ${dropped.length === 1 ? t('calendar.droppedOne') : `${dropped.length} ${t('calendar.droppedMany')}`}`}
            action={{ label: t('calendar.myCalendar'), onAction: () => setSelection(MY_CALENDAR) }}
          />
        ) : null}
        {/* 🔨 The calendar states the generic failure rather than the read's own message, so it keeps its own banner. */}
        {state.kind === 'failed' && <AlertBanner tone="error" message={t('async.failed')} action={{ label: t('async.retry'), onAction: reload }} />}
        <CalendarGrid
          monthStart={monthStart}
          weekOf={weekOf}
          events={gridEvents}
          timeZone={zone}
          today={today}
          scale={scale}
          ownerColors={ownerColors}
          holidays={holidays}
          team={teamControl}
          opened={opened}
          renderCard={renderCard}
          onOpenEvent={setOpened}
          onAddOnDay={(date) => {
            setOpened(null);
            setAddingOn(date);
          }}
        />
        {/*
          🔨 The modal reads `initial` once, when it mounts, so the key names
          what it is editing and a different row remounts it. 💰 Without the key
          the editor opened on the empty draft it was first rendered with, and
          an existing event's Start and End came up blank (found while building,
          2026-09-09).
        */}
        <EventModal
          key={windowKey}
          open={open}
          initial={initial}
          ownerPersonId={personId}
          organizerTimeZone={zone}
          places={places.state.kind === 'ready' ? places.state.data : []}
          searchPlaces={searchPlaces}
          attendees={attendees.state.kind === 'ready' ? attendees.state.data : []}
          attendeeNames={attendeeNamesOf(editing)}
          resolveSiteCascade={resolveSiteCascade}
          searchCompanies={searchLinked}
          searchPeople={searchPeople}
          onAddNewPlace={addPlace.onAddNewPlace}
          readMapsLink={addPlace.readMapsLink}
          existing={existing}
          reminder={seeded ?? (reminderAnswer.state.kind === 'ready' ? reminderAnswer.state.data : null)}
          onSave={(draft, scope) => void save(draft, scope)}
          {...(editing && !offersCancelMeeting(editing) ? {} : { onDelete: (scope: SeriesScope | null) => void cancel(scope) })}
          onReinstate={() => void reinstate()}
          onCancel={() => {
            setEditing(null);
            setAdding(false);
            setAddingOn(null);
          }}
        />
        {addPlace.siteModal}
      </div>
    </PageModule>
  );
}

/** Whether the modal asks the series question before it saves (decision 0108). */
export { needsSeriesScope };

/**
 * 🔒 The kinds another record writes and the calendar only reads (decision
 * 0118): a job's Scheduled Work, changed on its Work Order, and approved time
 * off, changed through its request (decision 13030). The server refuses an
 * edit or a cancel of either, so the calendar offers neither.
 */
function systemEvent(row: Pick<CalendarEventRow, 'eventType'>): boolean {
  return row.eventType === 'WorkOrderBlock' || row.eventType === 'PTO';
}

/**
 * 🔒 A job's Scheduled Work is called off on its Work Order, never here
 * (decision 2982; T2501), and approved time off through its request (decision
 * 13030): the editor offers no Cancel meeting on either kind, as the server
 * refuses it.
 */
export function offersCancelMeeting(row: Pick<CalendarEventRow, 'eventType'>): boolean {
  return !systemEvent(row);
}

/**
 * 🔒 A job's Scheduled Work is changed on its Work Order too, and approved time
 * off through its request; the server refuses a save on either kind, so the card
 * withholds Edit as it does for another person's event (decision 6463; T2503).
 */
export function offersEditMeeting(row: Pick<CalendarEventRow, 'eventType'>): boolean {
  return !systemEvent(row);
}

/** The sentence a card without Edit carries: where the event changes, or that it is somebody else's. */
export function readOnlyLine(row: Pick<CalendarEventRow, 'eventType'>): 'calendar.readOnly' | 'calendar.scheduledWork' | 'calendar.timeOff' {
  if (row.eventType === 'PTO') return 'calendar.timeOff';
  return row.eventType === 'WorkOrderBlock' ? 'calendar.scheduledWork' : 'calendar.readOnly';
}
